<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { ref, computed } from "vue";
import {
  useToast,
  AtomIcon,
  AtomText,
  AtomMoney,
  AtomSwitcher,
  SatTooltip,
  SatElasticPanel,
  AtomLoadingSpinner,
} from "@chatfood/design-system";
import { LoyaltyRedeemCashForm } from "../loyalty-redeem-cash-form";
import { useBasketStore } from "~/stores/basket";
import { useCustomerStore } from "~/stores/customer";
import { useLocale } from "~/composables/use-locale";
import { BASE_TELEPORT_SELECTOR } from "~/domain/base-teleport-selector";

const css = bemBuilder("loyalty-redeem-cash");

const { t } = useLocale();
const { toastError } = useToast();

const customer = useCustomerStore();
const basket = useBasketStore();

const cashWallet = computed(() => customer.loyalty.wallet.cash.balance);
const loyaltyWalletCashApplied = computed(() => basket.loyaltyWallet?.amount);

const creditsToBeUsed = computed(
  () => loyaltyWalletCashApplied.value ?? cashWallet.value,
);

const isLoading = ref(false);

const showModalForm = ref(false);

const invalidRedeemMessage = computed(() => {
  if (basket.discount) {
    return t("components.loyalty_redeem_cash.invalid_discount_applied");
  }

  const filterLoyalties = basket.loyalties.filter(
    (loyalty) => loyalty.category !== "loyalty_wallet_amount",
  );

  if (filterLoyalties.length) {
    return t("components.loyalty_redeem_cash.invalid_reward_applied");
  }

  return "";
});

const colorText = computed(() =>
  invalidRedeemMessage.value ? "color-text-disabled" : "color-text-body",
);

const invalidActions = computed(
  () =>
    Boolean(invalidRedeemMessage.value) ||
    isLoading.value ||
    basket.isProcessing,
);

const handleCredits = async () => {
  if (invalidActions.value) return;

  isLoading.value = true;

  try {
    if (!loyaltyWalletCashApplied.value) {
      await onApplyCredits();
    } else {
      await onRemoveCredits();
    }
  } finally {
    isLoading.value = false;
  }
};

const onApplyCredits = async () => {
  try {
    await basket.applyLoyaltyWalletCash(creditsToBeUsed.value);
  } catch {
    toastError(t("components.loyalty_redeem_cash.error_apply_toast"));
  }
};

const onRemoveCredits = async () => {
  try {
    await basket.removeLoyaltyWallet();
  } catch {
    toastError(t("components.loyalty_redeem_cash.error_remove_toast"));
  }
};

const openModalForm = () => {
  if (invalidActions.value) return;

  showModalForm.value = true;
};
</script>

<template>
  <div :class="css('')">
    <SatTooltip :content="invalidRedeemMessage">
      <div
        data-test="box"
        :class="[
          css('box'),
          { [css('box', 'disabled')]: Boolean(invalidRedeemMessage) },
        ]"
      >
        <a
          href="#"
          data-test="switcher"
          :class="css('action')"
          @click.prevent="handleCredits"
        >
          <span v-if="isLoading" :class="css('loading')">
            <AtomLoadingSpinner :size="21" />
          </span>
          <AtomSwitcher
            v-else
            :checked="Boolean(loyaltyWalletCashApplied)"
            :disabled="invalidActions"
            size="small"
          />
          <AtomText
            type="body-bold"
            :color="colorText"
            data-test="use-credits-label"
          >
            {{ t("components.loyalty_redeem_cash.credits_label") }}
          </AtomText>
        </a>
        <a
          href="#"
          :class="css('action')"
          data-test="edit-credits"
          @click.prevent="openModalForm"
        >
          <AtomIcon
            v-if="!invalidActions"
            name="pencil-circle"
            color="color-primary"
            data-test="edit-credits-icon"
          />
          <AtomText
            type="body-bold"
            :color="colorText"
            data-test="credits-label"
          >
            <AtomMoney
              :amount="creditsToBeUsed.value"
              :currency="creditsToBeUsed.currency"
            />
          </AtomText>
        </a>
      </div>
    </SatTooltip>
    <teleport :to="BASE_TELEPORT_SELECTOR">
      <SatElasticPanel v-model="showModalForm" show-close padding="none">
        <LoyaltyRedeemCashForm
          :class="css('modal-content')"
          @close="showModalForm = false"
        />
      </SatElasticPanel>
    </teleport>
  </div>
</template>

<style lang="scss" scoped>
.loyalty-redeem-cash {
  &__box {
    width: 100%;
    display: flex;
    align-items: center;
    gap: var(--spacing-md);
    padding: var(--spacing-md);
    justify-content: space-between;
    border-radius: var(--radii-card-sm);
    border: 1px solid var(--color-base-90);
    background: var(--color-base-100);

    &--disabled {
      pointer-events: none;
      cursor: no-drop;
      background: var(--color-base-95);
    }
  }

  &__action {
    display: flex;
    color: inherit;
    align-items: center;
    gap: var(--spacing-sm);
    cursor: pointer;
  }

  &__loading {
    display: flex;
    width: 34px;
    align-items: center;
    justify-content: center;
  }

  &__modal-content {
    padding: var(--spacing-lg);
  }
}
</style>
